/**
 * Muskel-Check (F-34 C2) - rein und getestet: Saetze und Trainingstage je
 * Muskel und Gruppe aus dem Protokoll, Vergleich mit Oles Zielen, Problemzonen,
 * Stagnation und Ungleichgewichte. Rein beschreibend - keine Ratschlaege.
 */
import { addDays, differenceInDays, startOfWeek } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';
import { beitragJeGruppe, beitragJeMuskel, GRUPPEN_IDS, MUSKEL_IDS, type GruppeId, type MuskelId } from '@/domain/training/muskeln';
import { kraftVerlauf } from '@/domain/training/auswertung';
import type { TrainingsProtokoll, Uebung } from '@/domain/training/typen';

export interface MuskelWert {
  /** Gewichtete Arbeitssaetze (Haupt 1, Hilf 0,5). */
  saetze: number;
  /** Tage mit Belastung. */
  tage: number;
  /** Uebungen, die beigetragen haben (IDs). */
  uebungen: string[];
  zuletzt: LocalDate | null;
}

export interface Volumen {
  muskeln: Record<MuskelId, MuskelWert>;
  gruppen: Record<GruppeId, MuskelWert>;
}

const neu = (): MuskelWert => ({ saetze: 0, tage: 0, uebungen: [], zuletzt: null });

/** Belastung je Muskel und Gruppe im Zeitraum (inklusive). Mobility, Aufwaermsaetze und Uebersprungenes zaehlen nicht. */
export function volumen(protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>, von: LocalDate, bis: LocalDate): Volumen {
  const m = Object.fromEntries(MUSKEL_IDS.map((k) => [k, neu()])) as Record<MuskelId, MuskelWert>;
  const g = Object.fromEntries(GRUPPEN_IDS.map((k) => [k, neu()])) as Record<GruppeId, MuskelWert>;
  const tageM = new Map<string, Set<LocalDate>>();
  const tageG = new Map<string, Set<LocalDate>>();
  for (const p of protokolle) {
    if (p.status !== 'fertig' || p.datum < von || p.datum > bis) continue;
    for (const pu of p.uebungen) {
      const u = bibliothek.get(pu.uebungId);
      if (!u || u.typ === 'mobility' || pu.uebersprungen) continue;
      const n = pu.saetze.filter((s) => s.erledigt && s.art !== 'aufwaermen').length;
      if (!n) continue;
      const eintragen = <K extends string>(ziel: Record<K, MuskelWert>, tage: Map<string, Set<LocalDate>>, beitrag: Map<K, number>) => {
        for (const [k, w] of beitrag) {
          const x = ziel[k];
          x.saetze += w * n;
          if (!x.uebungen.includes(u.id)) x.uebungen.push(u.id);
          if (!x.zuletzt || p.datum > x.zuletzt) x.zuletzt = p.datum;
          (tage.get(k) ?? tage.set(k, new Set()).get(k)!).add(p.datum);
        }
      };
      eintragen(m, tageM, beitragJeMuskel(u.hauptmuskeln, u.hilfsmuskeln));
      eintragen(g, tageG, beitragJeGruppe(u.hauptmuskeln, u.hilfsmuskeln));
    }
  }
  for (const [k, s] of tageM) m[k as MuskelId].tage = s.size;
  for (const [k, s] of tageG) g[k as GruppeId].tage = s.size;
  return { muskeln: m, gruppen: g };
}

/** Durchschnitt mehrerer Wochen - Saetze und Tage geteilt durch die Zahl der Wochen. */
export function schnitt(v: Volumen, wochen: number): Volumen {
  const teile = (x: MuskelWert): MuskelWert => ({ ...x, saetze: x.saetze / wochen, tage: x.tage / wochen });
  return {
    muskeln: Object.fromEntries(Object.entries(v.muskeln).map(([k, x]) => [k, teile(x)])) as Volumen['muskeln'],
    gruppen: Object.fromEntries(Object.entries(v.gruppen).map(([k, x]) => [k, teile(x)])) as Volumen['gruppen'],
  };
}

export type Zeitraum = 'woche' | 'vorwoche' | 'vier';

export function volumenFuer(protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>, heute: LocalDate, z: Zeitraum): Volumen {
  const mo = startOfWeek(heute);
  if (z === 'woche') return volumen(protokolle, bibliothek, mo, addDays(mo, 6));
  if (z === 'vorwoche') return volumen(protokolle, bibliothek, addDays(mo, -7), addDays(mo, -1));
  return schnitt(volumen(protokolle, bibliothek, addDays(mo, -28), addDays(mo, -1)), 4);
}

// --- Ziele und Status --------------------------------------------------------

export interface MuskelZiel { saetze: number; tage: number }

/**
 * Startwerte, frei aenderbar: 10 Saetze je Woche (Schoenfeld, Ogborn & Krieger
 * 2017, J Sports Sci 35:1073, Dosis-Wirkung: ab etwa 10 Saetzen je Woche
 * mehr Muskelzuwachs) und mindestens 2 Trainingstage (Oles Anspruch; vgl.
 * Schoenfeld et al. 2016, Sports Med 46:1689).
 */
export const STANDARD_ZIEL: MuskelZiel = { saetze: 10, tage: 2 };

export const zielVon = (ziele: Partial<Record<GruppeId, MuskelZiel>> | undefined, g: GruppeId): MuskelZiel => ziele?.[g] ?? STANDARD_ZIEL;

export type MuskelStatus = 'nicht' | 'deutlich_unter' | 'knapp_unter' | 'im_ziel' | 'ueber';

export const STATUS_TEXT: Record<MuskelStatus, string> = {
  nicht: 'nicht trainiert',
  deutlich_unter: 'deutlich unter Ziel',
  knapp_unter: 'knapp unter Ziel',
  im_ziel: 'im Ziel',
  ueber: 'über Ziel',
};

/**
 * Unter 50 % der Ziel-Saetze = deutlich unter; darunter oder zu wenige Tage =
 * knapp unter; ab 150 % = ueber Ziel. Die Schwellen sind bewusst einfach.
 */
export function status(w: Pick<MuskelWert, 'saetze' | 'tage'>, ziel: MuskelZiel): MuskelStatus {
  if (w.saetze <= 0) return 'nicht';
  const r = ziel.saetze > 0 ? w.saetze / ziel.saetze : 1;
  if (r < 0.5) return 'deutlich_unter';
  if (r < 1 || w.tage + 1e-9 < ziel.tage) return 'knapp_unter';
  if (r >= 1.5) return 'ueber';
  return 'im_ziel';
}

// --- Tabelle: vernachlaessigt, Problemzonen, Schwaechen -----------------------

export type Trend = 'hoch' | 'runter' | 'gleich';

export interface GruppenZeile {
  gruppe: GruppeId;
  saetze: number;
  ziel: MuskelZiel;
  differenz: number;
  tage: number;
  /** Tage seit dem letzten Training, `null` = noch nie. */
  vorTagen: number | null;
  trend: Trend;
  status: MuskelStatus;
  /** In 3 der letzten 4 abgeschlossenen Wochen unter Ziel. */
  problemzone: boolean;
}

/** Saetze je Woche der letzten 4 abgeschlossenen Wochen, aelteste zuerst. */
function letzteVierWochen(protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>, heute: LocalDate): Volumen[] {
  const mo = startOfWeek(heute);
  return [4, 3, 2, 1].map((n) => volumen(protokolle, bibliothek, addDays(mo, -7 * n), addDays(mo, -7 * n + 6)));
}

/** Steigung der vier Wochen: deutlich hoch, runter oder gleich (Toleranz 1 Satz). */
function trendVon(werte: number[]): Trend {
  const d = (werte[2]! + werte[3]!) / 2 - (werte[0]! + werte[1]!) / 2;
  return d > 1 ? 'hoch' : d < -1 ? 'runter' : 'gleich';
}

export function gruppenTabelle(protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>, heute: LocalDate, ziele?: Partial<Record<GruppeId, MuskelZiel>>): GruppenZeile[] {
  const jetzt = volumenFuer(protokolle, bibliothek, heute, 'woche');
  const vier = letzteVierWochen(protokolle, bibliothek, heute);
  const gesamt = volumen(protokolle, bibliothek, '0000-01-01', heute);
  return GRUPPEN_IDS.map((g) => {
    const ziel = zielVon(ziele, g);
    const w = jetzt.gruppen[g];
    const unter = vier.filter((v) => status(v.gruppen[g], ziel) !== 'im_ziel' && status(v.gruppen[g], ziel) !== 'ueber').length;
    const zuletzt = gesamt.gruppen[g].zuletzt;
    return {
      gruppe: g, saetze: w.saetze, ziel, differenz: w.saetze - ziel.saetze, tage: w.tage,
      vorTagen: zuletzt ? differenceInDays(zuletzt, heute) : null,
      trend: trendVon(vier.map((v) => v.gruppen[g].saetze)),
      status: status(w, ziel),
      problemzone: unter >= 3,
    };
  }).sort((a, b) => a.differenz - b.differenz);
}

export interface Stagnation { uebungId: string; letzterBestwert: LocalDate; wochen: number }

/**
 * Stagnation: Uebungen, die in den letzten 2 Wochen trainiert wurden, aber
 * seit mindestens `wochen` Wochen keinen Bestwert (1RM bzw. Wiederholungen)
 * mehr hatten.
 */
export function stagnation(protokolle: TrainingsProtokoll[], uebungIds: string[], heute: LocalDate, wochen = 4): Stagnation[] {
  const out: Stagnation[] = [];
  for (const id of uebungIds) {
    const v = kraftVerlauf(protokolle, id);
    if (v.length < 3) continue;
    const letzte = v[v.length - 1]!;
    if (differenceInDays(letzte.datum, heute) > 14) continue;
    const best = [...v].reverse().find((p) => p.bestwert)!;
    const her = Math.floor(differenceInDays(best.datum, heute) / 7);
    if (her >= wochen) out.push({ uebungId: id, letzterBestwert: best.datum, wochen: her });
  }
  return out.sort((a, b) => b.wochen - a.wochen);
}

export interface Ungleichgewicht { name: string; a: string; b: string; saetzeA: number; saetzeB: number; verhaeltnis: number }

const PAARE: { name: string; a: string; b: string; ga: GruppeId[]; gb: GruppeId[] }[] = [
  { name: 'Drücken vs. Ziehen', a: 'Drücken', b: 'Ziehen', ga: ['brust', 'schulter_vorne', 'trizeps'], gb: ['latissimus', 'trapez', 'schulter_hinten', 'bizeps'] },
  { name: 'Vorder- vs. Rückseite', a: 'Vorderseite', b: 'Rückseite', ga: ['brust', 'schulter_vorne', 'bizeps', 'bauch', 'quadrizeps', 'hueftbeuger'], gb: ['latissimus', 'trapez', 'schulter_hinten', 'trizeps', 'unterer_ruecken', 'gesaess', 'beinbeuger'] },
  { name: 'Quadrizeps vs. Beinbeuger', a: 'Quadrizeps', b: 'Beinbeuger', ga: ['quadrizeps'], gb: ['beinbeuger'] },
];

/** Ungleichgewichte der letzten 4 Wochen ab `schwelle` (Standard 1,5) in eine Richtung. */
export function ungleichgewichte(v: Volumen, schwelle = 1.5): Ungleichgewicht[] {
  const out: Ungleichgewicht[] = [];
  for (const p of PAARE) {
    const a = p.ga.reduce((s, g) => s + v.gruppen[g].saetze, 0);
    const b = p.gb.reduce((s, g) => s + v.gruppen[g].saetze, 0);
    if (a + b < 4) continue;
    const r = Math.max(a, b) / Math.max(0.5, Math.min(a, b));
    if (r >= schwelle) out.push({ name: p.name, a: p.a, b: p.b, saetzeA: Math.round(a * 10) / 10, saetzeB: Math.round(b * 10) / 10, verhaeltnis: Math.round(r * 10) / 10 });
  }
  return out;
}

/** Passende Uebungen fuer eine Gruppe: Hauptmuskel in der Gruppe, Mobility ausgenommen. */
export function uebungenFuer(g: GruppeId, bibliothek: ReadonlyMap<string, Uebung>): Uebung[] {
  return [...bibliothek.values()].filter((u) => !u.archiviert && u.typ !== 'mobility' && u.hauptmuskeln.some((m) => beitragJeGruppe([m], []).has(g)));
}
